import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

import type { AuthResponse } from '../../types/auth-response';
import type { CreateAccountReq } from '../../types/create-account-req';

import { Environment } from '../../../environments/environment';
import { StateService } from '../state/state';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _stateService = inject(StateService);

  constructor() {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    // Read the token from session storage on service initialization and update the state accordingly.
    const _token = sessionStorage.getItem('tv_auth_token');
    const _username = sessionStorage.getItem('tv_username');

    if (_token) {
      this._stateService.userToken.set(_token);
    }

    if (_username) {
      this._stateService.username.set(_username);
    }
  }

  private _errorToast(message: string, title: string): void {
    this._stateService.toastMessage.set({
      duration: 4000,
      icon: 'error',
      message,
      title,
    });
  }

  private async _request(body: string, path: string): Promise<Response> {
    const _result = await fetch(`https://api.v2.thevillages.com/${path}/`, {
      body,
      headers: {
        Authorization: Environment.basicToken,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- This is the correct header name for content type.
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    return _result;
  }

  public async createAccount(data: CreateAccountReq): Promise<Response | undefined> {
    const _result = await this._request(JSON.stringify(data), 'users');

    if (!_result.ok) {
      // Display an error toast message if the submission fails.
      this._errorToast(
        'There was an error creating your account. Please try again later.',
        'Account Creation Failed',
      );

      return;
    }

    return _result;
  }

  public async forgotPassword(data: { username: string }): Promise<boolean> {
    const _result = await this._request(JSON.stringify(data), 'reset-password');

    if (!_result.ok) {
      this._errorToast(
        'There was an error reseting your password. Please try again later.',
        'Reset Password Failed',
      );

      return false;
    }

    return true;
  }

  public async login(data: { password: string; username: string }): Promise<boolean> {
    // Submit the form data to the API.
    const _loginResult = await this._request(JSON.stringify(data), 'auth'); // TODO: should be typed

    if (!_loginResult.ok) {
      this._errorToast('Incorrect username and/or password. Please try again.', 'Login Failed');

      return false;
    }

    const _loginResultJson: AuthResponse = (await _loginResult.json()) as AuthResponse;

    const _username = _loginResultJson.displayName;

    sessionStorage.setItem('tv_username', _username);
    this._stateService.userToken.set(_loginResultJson.token);
    this._stateService.username.set(_username);

    const _tokenObject = {
      data: { data: { token: _loginResultJson.token } },
    };

    this._stateService.handleAuthPromise(_tokenObject as MessageEvent);

    return true;
  }

  public logout(): void {
    sessionStorage.removeItem('tv_auth_token');
    sessionStorage.removeItem('tv_username');
    this._stateService.userToken.set(undefined);
    this._stateService.username.set(undefined);
    this._stateService.notifyParentOfStateChange('HF_EVENT:LOGOUT');
  }
}
